import { getAuth } from 'firebase/auth';
import loadUser from '../auth/loadUser';
import config, { firebaseApp } from '../config';

const { apiBase, streamServerBase } = config;

export const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

// This generates the base headers for authentication
// It does not include JSON headers, since useAuthFetch needs to support non-JSON requests
export const getBaseHeaders = async (): Promise<Record<string, string>> => {
  let token;
  try {
    await loadUser();
    token = await getAuth(firebaseApp).currentUser?.getIdToken();
  } catch (error) {
    console.error('Failed to get token', error);
  }

  if (token) return { Authorization: `Bearer ${token}` };
  return {};
};

// This wraps fetch with the Firebase auth token and JSON handling
const authFetchFactory = (fetchBase: string) =>
  async function <B extends boolean>(
    url: string,
    { headers: customHeaders, ...options }: RequestInit = {},
    isJson?: B,
  ): Promise<B extends true ? any : Response> {
    const headers = {
      ...(await getBaseHeaders()),
      // Allow not setting content-type (needed for multipart form uploads or downloads)
      ...(isJson !== false ? jsonHeaders : {}),
      // Spread user-defined headers *after* to allow overrides
      ...customHeaders,
    };

    const fetchOptions = { ...options, headers };
    const response = await fetch(`${fetchBase}${url}`, fetchOptions);
    // Do not attempt to parse JSON if 503 is returned: https://github.com/GriffinScribe-LLC/recap-web/issues/63
    if (response.status === 503) {
      throw new Error(
        'A server error occurred, and our team has been notified. Please try again later.',
      );
    }
    if (isJson === false) {
      if (!response.ok) {
        throw response;
      }
      return response;
    }

    // Handle 204 No Content responses which have no body to parse
    if (response.status === 204) {
      return null as any;
    }

    const json = await response.json();
    if (!response.ok) {
      throw json || response.status;
    } else {
      return json;
    }
  };

// For cloud functions (almost all requests)
export const authFetch = authFetchFactory(apiBase);

// For livestream captioning server
export const captionServerAuthFetch = authFetchFactory(streamServerBase);
